import { ref } from "vue";
import { routingFetch } from "@app/data/helpers";
import { useTrpc } from "@app/vue/providers/index";
import { prepareDocument, PrepareError, type PreparedDocument } from "./prepareDocument";
import type { ScanKind, ScanResult } from "./types";

export type ScanStep = "prepare" | "upload" | "read" | "match";
export const SCAN_STEPS: ScanStep[] = ["prepare", "upload", "read", "match"];

// Prepare pages → keep the original → read with AI (matching runs in the
// same call on the server; shown as its own step once the AI has answered).
export function useScan() {
  const trpc = useTrpc();
  const step = ref<ScanStep | null>(null);
  const prepared = ref<PreparedDocument | null>(null);
  const result = ref<ScanResult | null>(null);
  const error = ref<string | null>(null);

  const upload = async (file: File): Promise<string | null> => {
    const body = new FormData();
    body.append("file", file);
    const res = await routingFetch("/api/purchasing/documents", { method: "POST", body, credentials: "include" });
    if (!res.ok) {
      const msg = ((await res.json().catch(() => null)) as { error?: string } | null)?.error;
      throw new Error(msg ?? `Upload failed (${res.status})`);
    }
    return ((await res.json()) as { key: string | null }).key;
  };

  const run = async (files: File[], kind: ScanKind) => {
    error.value = null;
    result.value = null;
    try {
      step.value = "prepare";
      prepared.value = await prepareDocument(files);

      step.value = "upload";
      // Keeping the original is best effort: reading still works without it.
      const key = await upload(prepared.value.original).catch(() => null);

      step.value = "read";
      const reading = trpc.purchasing.scanDocument.mutate({
        kind,
        images: prepared.value.pages,
        document_key: key,
      });
      result.value = (await reading) as ScanResult;
      step.value = "match";
      await new Promise((r) => setTimeout(r, 350));
      step.value = null;
    } catch (e) {
      step.value = null;
      error.value =
        e instanceof PrepareError ? e.message : ((e as { message?: string })?.message ?? "scan_failed");
    }
  };

  const reset = () => {
    step.value = null;
    prepared.value = null;
    result.value = null;
    error.value = null;
  };

  return { step, prepared, result, error, run, reset };
}
